/**
 * Cloudflare Turnstile helper for VetConsult surfaces.
 *
 * When VUE_APP_TURNSTILE_SITE_KEY is unset, turnsstile is a no-op so local
 * / preview builds keep working (backend also skips verify without a secret).
 */

import { ref, onBeforeUnmount } from 'vue'

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

let scriptLoadPromise = null

function ensureTurnstileScript() {
  if (typeof window === 'undefined') {
    return Promise.resolve(null)
  }
  if (window.turnstile) {
    return Promise.resolve(window.turnstile)
  }
  if (scriptLoadPromise) {
    return scriptLoadPromise
  }
  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve(window.turnstile))
      existing.addEventListener('error', reject)
      if (window.turnstile) resolve(window.turnstile)
      return
    }
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve(window.turnstile)
    script.onerror = reject
    document.head.appendChild(script)
  })
  return scriptLoadPromise
}

/**
 * @param {{ siteKey?: string, containerRef: import('vue').Ref<HTMLElement|null> }} options
 */
export function useTurnstile({ siteKey = '', containerRef } = {}) {
  const token = ref('')
  const widgetId = ref(null)
  const ready = ref(false)
  const resolvedSiteKey = siteKey || process.env.VUE_APP_TURNSTILE_SITE_KEY || ''

  async function mount() {
    if (!resolvedSiteKey || !containerRef?.value) {
      ready.value = !resolvedSiteKey
      return
    }
    try {
      const turnstile = await ensureTurnstileScript()
      if (!turnstile || !containerRef.value) return
      if (widgetId.value !== null) {
        try {
          turnstile.remove(widgetId.value)
        } catch {
          /* noop */
        }
        widgetId.value = null
      }
      widgetId.value = turnstile.render(containerRef.value, {
        sitekey: resolvedSiteKey,
        callback: (value) => {
          token.value = value || ''
        },
        'error-callback': () => {
          token.value = ''
        },
        'expired-callback': () => {
          token.value = ''
        },
      })
      ready.value = true
    } catch {
      ready.value = false
    }
  }

  function reset() {
    token.value = ''
    if (typeof window === 'undefined' || !window.turnstile || widgetId.value === null) {
      return
    }
    try {
      window.turnstile.reset(widgetId.value)
    } catch {
      /* noop */
    }
  }

  function requiresToken() {
    return Boolean(resolvedSiteKey)
  }

  function canSubmit() {
    if (!resolvedSiteKey) return true
    return Boolean(token.value)
  }

  onBeforeUnmount(() => {
    if (typeof window === 'undefined' || !window.turnstile || widgetId.value === null) {
      return
    }
    try {
      window.turnstile.remove(widgetId.value)
    } catch {
      /* noop */
    }
    widgetId.value = null
  })

  return {
    siteKey: resolvedSiteKey,
    token,
    widgetId,
    ready,
    mount,
    reset,
    requiresToken,
    canSubmit,
  }
}
